/**
 * The one place a referral code is normalised, and the one place its shape is
 * judged.
 *
 * A hand-typed code and a link-resolved code must be indistinguishable
 * downstream (GOTU-7244), so every entry point runs through here and no caller
 * normalises again. Mirrors mobile-app `src/domain/Referral/referralCode.ts`.
 *
 * This file used to argue against holding a copy of referral-service's
 * alphabet: widen it server-side and the portal would reject codes it actually
 * issues, reporting a real code as not found. That reasoning still holds — it
 * is simply outranked. Per the 2026-09-22 decision with referral-service, the
 * by-code lookup sits behind an enumeration breaker, and an unconstrained
 * request per submit is itself the pattern the breaker watches for. A tripped
 * breaker answers `unavailable` for *everyone*, so being permissive no longer
 * costs one wasted request — it risks the endpoint for every referred office.
 *
 * So the alphabet is mirrored here on purpose, with the drift accepted and
 * named: `internal/referralcode/alphabet.go` is the source of truth, and
 * widening it server-side now REQUIRES a matching change here. Nothing else in
 * the portal may format-check a code — `referralSignUpGate` and
 * `useReferrerByCode` both ask this module.
 */

/**
 * Upper-cases and strips whitespace and hyphens, so a pasted `ua j4-dj` and a
 * typed `UAJ4DJ` produce the same value. Runs before anything is sent.
 */
export const normalizeReferralCode = (raw: string | null | undefined): string =>
  (raw ?? '').replace(/[\s-]/g, '').toUpperCase();

/**
 * Whether `code` could be a referral code at all: six characters drawn from
 * `23456789ABCDEFGHJKLMNPQRSTUVWXYZ`, the alphabet referral-service issues from
 * — no `0`/`O` and no `1`/`I`, so a code cannot be misheard or misread.
 *
 * Expects an **already-normalised** code, and is deliberately case- and
 * whitespace-sensitive so a caller cannot accept ` uaj4dj ` here and then send
 * something else. Run `normalizeReferralCode` first; both callers do.
 *
 * One literal regex rather than a class built from an alphabet constant: these
 * ranges are what someone diffs against the Go source when it changes, and a
 * constructed pattern hides them. `2-9` drops `0` and `1`; `A-H`, `J-N` and
 * `P-Z` drop `I` and `O`.
 */
export const isReferralCodeFormatValid = (code: string): boolean =>
  /^[2-9A-HJ-NP-Z]{6}$/.test(code);

/**
 * The code a v2 referral link carries, decoded but not yet normalised, or
 * `null` when `url` has neither link shape.
 *
 * A v2 link reaches the portal in one of two shapes, and both must yield the
 * same code (GOTU-7432, mirroring mobile's GOTU-7308):
 *
 * - the alias path, `https://<branch-host>/referrals/v2/<CODE>` — the URL as
 *   shared, and what Branch's desktop fallback forwards;
 * - the Branch `$deeplink_path` referral-service sets on that link (GOTU-7140),
 *   `referral?referralCode=<CODE>`.
 *
 * The path segment is tried first.
 */
const readReferralLinkCode = (url: string | null | undefined): string | null => {
  const raw =
    (url ?? '').match(/\/referrals\/v2\/([^/?#\s]+)/)?.[1] ??
    (url ?? '').match(/[?&]referralCode=([^&#\s]*)/)?.[1];
  // Load-bearing: the query pattern matches an empty value
  // (`?referralCode=`), and this is what stops that empty string being treated
  // as a code. `isReferralCodeFormatValid` is no help here — the referral-link
  // path deliberately never runs it (see below).
  if (!raw) return null;

  // `decodeURIComponent` throws on a malformed escape (`%E0%A4%A`); a bad link
  // must land cleanly, so treat that as "no code".
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
};

/**
 * Pulls the code out of a v2 referral URL — see `readReferralLinkCode` for the
 * two shapes it reads.
 *
 * Returns the normalised code, or `null` when the URL carries neither shape.
 *
 * Whatever a link does carry is passed through **unjudged** — deliberately not
 * `isReferralCodeFormatValid`-checked. A link is a real arrival even when its
 * code is junk, and per GOTU-7141 that row is the denominator. A bad code from
 * a link therefore reaches the field, and the format check blocks it at submit
 * instead — which is the intended split: the shape rule guards the lookup
 * endpoint, not the claim table.
 */
export const extractReferralCodeFromUrl = (url: string | null | undefined): string | null =>
  normalizeReferralCode(readReferralLinkCode(url)) || null;

/**
 * What the referral code field should hold after `text` was entered into it.
 *
 * A referred office often has the whole link on the clipboard rather than the
 * code, and pasting `https://<branch-host>/referrals/v2/UAJ4DJ` into a
 * six-character field leaves a wall of URL that then fails the lookup gate. So
 * a link collapses to the code it carries, normalised the way a link-resolved
 * code is; anything else is returned as typed, since the field is edited
 * character by character and normalisation of hand input happens on submit.
 *
 * A link with a malformed code still collapses to that code, so what the user
 * sees in the field is what the gate will report on.
 */
export const referralCodeFromInput = (text: string): string => {
  const linkCode = readReferralLinkCode(text);

  return linkCode === null ? text : normalizeReferralCode(linkCode);
};
